import { lazy, Suspense, type JSX } from 'react';
import { createBrowserRouter, Navigate, useParams } from 'react-router-dom';

import { AppShell } from '@/components/layout/AppShell';
import { RootErrorFallback } from '@/components/errors/ErrorBoundary';
import { ForcePasswordChangeGuard } from '@/lib/auth/ForcePasswordChangeGuard';
import { PrivateRoute } from '@/lib/auth/PrivateRoute';
import { RequirePermission } from '@/lib/auth/RequirePermission';
import { RequireRole } from '@/lib/auth/RequireRole';
import { ApproverHistoryPage } from '@/pages/approver/ApproverHistoryPage';
import { ApproverPendingListPage } from '@/pages/approver/ApproverPendingListPage';
import { ChangePasswordPage } from '@/pages/ChangePasswordPage';
import { CheckerHistoryPage } from '@/pages/checker/CheckerHistoryPage';
import { CheckerPendingListPage } from '@/pages/checker/CheckerPendingListPage';
import { LogsheetDetailPage } from '@/pages/logsheets-view/LogsheetDetailPage';
import { ClientCreatePage } from '@/pages/clients/ClientCreatePage';
import { ClientDetailPage } from '@/pages/clients/ClientDetailPage';
import { ClientEditPage } from '@/pages/clients/ClientEditPage';
import { ClientsListPage } from '@/pages/clients/ClientsListPage';
import { Dashboard } from '@/pages/Dashboard';
import { DiagnosticsPage } from '@/pages/DiagnosticsPage';
import { ForcePasswordChangePage } from '@/pages/ForcePasswordChangePage';
import { KpiTemplatesListPage } from '@/pages/kpi-templates/KpiTemplatesListPage';
import { LoginForm } from '@/pages/LoginForm';
import { CreateMakerLogsheetBulkWizardPage } from '@/pages/logsheets-maker/CreateMakerLogsheetBulkWizardPage';
import { CreateMakerLogsheetWizardPage } from '@/pages/logsheets-maker/CreateMakerLogsheetWizardPage';
import { MakerLogsheetEditPage } from '@/pages/logsheets-maker/MakerLogsheetEditPage';
import { MakerLogsheetsListPage } from '@/pages/logsheets-maker/MakerLogsheetsListPage';
import { MachineTypeCreatePage } from '@/pages/machine-types/MachineTypeCreatePage';
import { MachineTypeDetailPage } from '@/pages/machine-types/MachineTypeDetailPage';
import { MachineTypeEditPage } from '@/pages/machine-types/MachineTypeEditPage';
import { MachineTypesListPage } from '@/pages/machine-types/MachineTypesListPage';
import { MachineCreatePage } from '@/pages/machines/MachineCreatePage';
import { MachineDetailPage } from '@/pages/machines/MachineDetailPage';
import { MachineEditPage } from '@/pages/machines/MachineEditPage';
import { MachinesListPage } from '@/pages/machines/MachinesListPage';
import { MachineMonthlyEntriesPage } from '@/pages/monthly/MachineMonthlyEntriesPage';
import { MonthlyDocumentUploadPage } from '@/pages/monthly/MonthlyDocumentUploadPage';
import { MonthlySummaryPage } from '@/pages/monthly/MonthlySummaryPage';
import { NotFound } from '@/pages/NotFound';
import { AlertsListPage } from '@/pages/notifications/AlertsListPage';
import { NotificationSettingsPage } from '@/pages/notifications/NotificationSettingsPage';
import { RoleCreatePage } from '@/pages/roles/RoleCreatePage';
import { RoleDetailPage } from '@/pages/roles/RoleDetailPage';
import { RoleEditPage } from '@/pages/roles/RoleEditPage';
import { RolesListPage } from '@/pages/roles/RolesListPage';
import { SiteCreatePage } from '@/pages/sites/SiteCreatePage';
import { SiteDetailPage } from '@/pages/sites/SiteDetailPage';
import { SiteEditPage } from '@/pages/sites/SiteEditPage';
import { SitesListPage } from '@/pages/sites/SitesListPage';
import { SuperAdminDashboardPage } from '@/pages/super-admin/SuperAdminDashboardPage';
import { UserCreatePage } from '@/pages/users/UserCreatePage';
import { UserDetailPage } from '@/pages/users/UserDetailPage';
import { UserEditPage } from '@/pages/users/UserEditPage';
import { UsersListPage } from '@/pages/users/UsersListPage';
import { WorkOrderCreateWizard } from '@/pages/work-orders/WorkOrderCreateWizard';
import { WorkOrderDetailPage } from '@/pages/work-orders/WorkOrderDetailPage';
import { WorkOrderEditPage } from '@/pages/work-orders/WorkOrderEditPage';
import { WorkOrdersListPage } from '@/pages/work-orders/WorkOrdersListPage';

// Phase 25 (Plan 25-04) — Logsheet Report page + Export Job Tracker.
// Lazy-loaded so the entire report surface (with its 5 view-mode chunks)
// stays out of the initial-entry bundle. RESEARCH §Pattern 6.
const LogsheetReportPage = lazy(() => import('@/pages/reports/LogsheetReportPage'));
const ExportJobTrackerPage = lazy(() => import('@/pages/reports/ExportJobTrackerPage'));

/**
 * Phase 26 — redirect helper for legacy role-specific detail/review URLs. The
 * three pages (`MakerLogsheetDetailPage`, `CheckerReviewPage`, `ApproverReviewPage`)
 * have been replaced by the unified `LogsheetDetailPage` mounted at
 * `/logsheets/view/:id`. We keep redirects so any in-flight bookmarks survive.
 */
function RedirectToView(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/logsheets/view/${id ?? ''}`} replace />;
}

/**
 * Plan 06-03 router table.
 *
 *  /login                       → LoginForm (replaces Phase 3 LoginStub)
 *  /force-change-password       → PrivateRoute → ForcePasswordChangePage (full-screen, no AppShell)
 *  /                            → PrivateRoute → ForcePasswordChangeGuard → AppShell
 *      /dashboard               → Dashboard
 *      /settings/change-password → ChangePasswordPage
 *  *                            → NotFound
 *
 * The ForcePasswordChangeGuard wraps the AppShell subtree so that any user with
 * `passwordChangeRequired === true` is redirected to /force-change-password
 * BEFORE the AppShell renders — preventing the user from interacting with any
 * authed page until the password is rotated.
 */
export const appRouter = createBrowserRouter([
  {
    path: '/login',
    element: <LoginForm />,
  },
  {
    path: '/force-change-password',
    element: (
      <PrivateRoute>
        <ForcePasswordChangePage />
      </PrivateRoute>
    ),
  },
  {
    path: '/',
    element: (
      <PrivateRoute>
        <ForcePasswordChangeGuard>
          <AppShell />
        </ForcePasswordChangeGuard>
      </PrivateRoute>
    ),
    // REVIEW FF-004: catch uncaught render errors anywhere under the AppShell
    // tree (lazy-chunk load fail, parseKpiEntries throw, etc.). Replaces the
    // white-screen default.
    errorElement: <RootErrorFallback />,
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: 'dashboard', element: <Dashboard /> },
      // Phase 23 Plan 23-03 D-243: Super-Admin-only Diagnostics — relocated
      // Phase-3 health pill + DemoForm. Non-Super-Admin URL-typers redirect
      // to /dashboard via RequireRole.
      {
        path: 'diagnostics',
        element: (
          <RequireRole
            /* eslint-disable-next-line jsx-a11y/aria-role */
            role="Super Admin"
          >
            <DiagnosticsPage />
          </RequireRole>
        ),
      },
      // UNLINKED Super-Admin console — no nav entry anywhere. RequireRole is a
      // UX/obscurity gate only; the backend [Authorize(Roles = "Super Admin")]
      // on every /api/issue-reports + /api/admin/analytics endpoint is the real
      // security boundary. URL-typers without the role redirect to /dashboard.
      {
        path: 'super-admin',
        element: (
          <RequireRole
            /* eslint-disable-next-line jsx-a11y/aria-role */
            role="Super Admin"
          >
            <SuperAdminDashboardPage />
          </RequireRole>
        ),
      },
      { path: 'settings/change-password', element: <ChangePasswordPage /> },
      // Phase 7 Plan 07-02 — role-management surface.
      { path: 'roles', element: <RolesListPage /> },
      { path: 'roles/new', element: <RoleCreatePage /> },
      { path: 'roles/:id', element: <RoleDetailPage /> },
      { path: 'roles/:id/edit', element: <RoleEditPage /> },
      // Phase 8 Plan 08-02 — user-management surface.
      { path: 'users', element: <UsersListPage /> },
      { path: 'users/new', element: <UserCreatePage /> },
      { path: 'users/:id', element: <UserDetailPage /> },
      { path: 'users/:id/edit', element: <UserEditPage /> },
      // Phase 9 Plan 09-02 — site-management surface.
      { path: 'sites', element: <SitesListPage /> },
      { path: 'sites/new', element: <SiteCreatePage /> },
      { path: 'sites/:id', element: <SiteDetailPage /> },
      { path: 'sites/:id/edit', element: <SiteEditPage /> },
      // Phase 10 Plan 10-02 — machine-type-management surface.
      { path: 'machine-types', element: <MachineTypesListPage /> },
      { path: 'machine-types/new', element: <MachineTypeCreatePage /> },
      { path: 'machine-types/:id', element: <MachineTypeDetailPage /> },
      { path: 'machine-types/:id/edit', element: <MachineTypeEditPage /> },
      // Phase 11 Plan 11-02 — machine-management + site-allocation surface.
      { path: 'machines', element: <MachinesListPage /> },
      { path: 'machines/new', element: <MachineCreatePage /> },
      { path: 'machines/:id', element: <MachineDetailPage /> },
      { path: 'machines/:id/edit', element: <MachineEditPage /> },
      // Phase 12 Plan 12-03 — Maker logsheet surface.
      // REVIEW FF-006: route-level RequirePermission gates so each page never
      // mounts (and fires its `useQuery` hooks) for users without the relevant
      // logsheet.maker.* permission. The in-page `if (!permissions.includes(...))`
      // checks (now belt-and-braces) still execute, but the doomed API requests
      // and 401/403 log noise are eliminated at the route layer.
      {
        path: 'logsheets/maker',
        element: (
          <RequirePermission permission="logsheet.maker.read">
            <MakerLogsheetsListPage />
          </RequirePermission>
        ),
      },
      {
        path: 'logsheets/maker/new',
        element: (
          <RequirePermission permission="logsheet.maker.create">
            <CreateMakerLogsheetWizardPage />
          </RequirePermission>
        ),
      },
      // Phase 24 (v1.4) — Bulk Logsheet Create wizard.
      // Phase 24 (v1.4) Plan/REVIEW L-2: slug aligned with existing maker family
      // (`/logsheets/maker/*`). The old `/logsheets-maker/bulk-create` slug was a
      // BULK-15 SPEC drift that future devs would 404 on; renamed before any real
      // bookmarks exist.
      {
        path: 'logsheets/maker/bulk-create',
        element: (
          <RequirePermission permission="logsheet.maker.create">
            <CreateMakerLogsheetBulkWizardPage />
          </RequirePermission>
        ),
      },
      // Phase 26 — unified detail. Maker / Checker / Approver / Monthly all link here.
      {
        path: 'logsheets/view/:id',
        element: (
          <RequirePermission permission="logsheet.view">
            <LogsheetDetailPage />
          </RequirePermission>
        ),
      },
      // Phase 26 legacy redirect — keep bookmarks resolving to the unified page.
      {
        path: 'logsheets/maker/:id',
        element: <RedirectToView />,
      },
      {
        path: 'logsheets/maker/:id/edit',
        element: (
          <RequirePermission permission="logsheet.maker.update">
            <MakerLogsheetEditPage />
          </RequirePermission>
        ),
      },
      // Phase 13 Plan 13-02 — Checker logsheet surface.
      // REVIEW FF-007: route-level RequirePermission gates so each page never
      // mounts (and fires its `useQuery` hooks) for users without the relevant
      // logsheet.checker.* permission. History page uses the
      // `logsheet.checker.read-history` permission (verified against the page's
      // in-page guard); pending list + review pages share
      // `logsheet.checker.review`.
      {
        path: 'logsheets/checker/pending',
        element: (
          <RequirePermission permission="logsheet.checker.review">
            <CheckerPendingListPage />
          </RequirePermission>
        ),
      },
      // Phase 26 legacy redirect — checker review pages now resolve to the unified detail.
      {
        path: 'logsheets/checker/pending/:id',
        element: <RedirectToView />,
      },
      {
        path: 'logsheets/checker/history',
        element: (
          <RequirePermission permission="logsheet.checker.read-history">
            <CheckerHistoryPage />
          </RequirePermission>
        ),
      },
      // Phase 14 Plan 14-02 — Approver logsheet surface.
      // REVIEW FF-007: route-level RequirePermission gates — symmetric with the
      // checker family above. History page uses `logsheet.approver.read-history`
      // (verified against the page's in-page guard); pending list + review pages
      // share `logsheet.approver.review`.
      {
        path: 'logsheets/approver/pending',
        element: (
          <RequirePermission permission="logsheet.approver.review">
            <ApproverPendingListPage />
          </RequirePermission>
        ),
      },
      // Phase 26 legacy redirect — approver review pages now resolve to the unified detail.
      {
        path: 'logsheets/approver/pending/:id',
        element: <RedirectToView />,
      },
      {
        path: 'logsheets/approver/history',
        element: (
          <RequirePermission permission="logsheet.approver.read-history">
            <ApproverHistoryPage />
          </RequirePermission>
        ),
      },
      // Phase 15 Plan 15-02 — Monthly Summary + Month-Lock surface.
      // REVIEW FF-006: route-level RequirePermission gates so the pages never
      // mount (and never fire their `useQuery` hooks) for users without the
      // relevant `logsheet.monthly.*` permission. Verified against each page's
      // in-page guard — note the upload page uses `upload-documents`, NOT
      // `upload`.
      {
        path: 'logsheets/monthly-summary',
        element: (
          <RequirePermission permission="logsheet.monthly.read">
            <MonthlySummaryPage />
          </RequirePermission>
        ),
      },
      {
        path: 'logsheets/monthly-summary/:machineId',
        element: (
          <RequirePermission permission="logsheet.monthly.read">
            <MachineMonthlyEntriesPage />
          </RequirePermission>
        ),
      },
      {
        path: 'logsheets/monthly-documents/upload',
        element: (
          <RequirePermission permission="logsheet.monthly.upload-documents">
            <MonthlyDocumentUploadPage />
          </RequirePermission>
        ),
      },
      // Phase 22 Plan 22-03 — Client Management surface.
      { path: 'clients', element: <ClientsListPage /> },
      { path: 'clients/new', element: <ClientCreatePage /> },
      { path: 'clients/:id', element: <ClientDetailPage /> },
      { path: 'clients/:id/edit', element: <ClientEditPage /> },
      // Phase 16 Plan 16-03 — Work Order Management surface.
      // REVIEW FF-006: route-level RequirePermission gates so each page never
      // mounts (and fires its `useQuery` hooks) for users without the relevant
      // `workorders.*` permission. Verified against each page's in-page guard —
      // note the edit page uses `workorders.edit`, NOT `workorders.update`.
      {
        path: 'work-orders',
        element: (
          <RequirePermission permission="workorders.view">
            <WorkOrdersListPage />
          </RequirePermission>
        ),
      },
      {
        path: 'work-orders/new',
        element: (
          <RequirePermission permission="workorders.create">
            <WorkOrderCreateWizard />
          </RequirePermission>
        ),
      },
      {
        path: 'work-orders/:id',
        element: (
          <RequirePermission permission="workorders.view">
            <WorkOrderDetailPage />
          </RequirePermission>
        ),
      },
      {
        path: 'work-orders/:id/edit',
        element: (
          <RequirePermission permission="workorders.edit">
            <WorkOrderEditPage />
          </RequirePermission>
        ),
      },
      // Phase 17 Plan 17-02 — KPI Templates admin surface.
      // REVIEW FF-025: route-level RequirePermission replaces the in-page
      // `useEffect → navigate('/dashboard')` pattern, which rendered one frame
      // of the page (and fired `useKpiTemplatesQuery`) before redirecting.
      // The useEffect has been removed from the page itself.
      {
        path: 'kpi-templates',
        element: (
          <RequirePermission permission="kpis.manage_templates">
            <KpiTemplatesListPage />
          </RequirePermission>
        ),
      },
      // Phase 20 Plan 20-02 — Alerts list + Notification Settings admin surface.
      // REVIEW FF-003: route-level RequirePermission gate so the page never
      // mounts (and fires `useAlertsQuery`) for users without
      // `notifications.view`. The settings page already short-circuits on
      // page-level — gating at the route layer keeps the rule symmetric.
      {
        path: 'alerts',
        element: (
          <RequirePermission permission="notifications.view">
            <AlertsListPage />
          </RequirePermission>
        ),
      },
      {
        path: 'notification-settings',
        element: (
          <RequirePermission permission="notifications.manage_settings">
            <NotificationSettingsPage />
          </RequirePermission>
        ),
      },
      // Phase 25 Plan 25-04 — Logsheet Report multi-lens surface.
      // Gated by `reports.logsheet.view`; sidebar entry only visible to holders.
      // The async-job tracker shares the same permission gate.
      {
        path: 'reports/logsheet',
        element: (
          <RequirePermission permission="reports.logsheet.view">
            <Suspense fallback={<div>Loading…</div>}>
              <LogsheetReportPage />
            </Suspense>
          </RequirePermission>
        ),
      },
      {
        path: 'reports/logsheet/exports',
        element: (
          <RequirePermission permission="reports.logsheet.view">
            <Suspense fallback={<div>Loading…</div>}>
              <ExportJobTrackerPage />
            </Suspense>
          </RequirePermission>
        ),
      },
      // Future feature phases push their routes here under the same AppShell layout.
    ],
  },
  {
    path: '*',
    element: <NotFound />,
  },
]);
